import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Drawer } from "antd";
import {
  Article,
  Books,
  DotsThreeOutline,
  EnvelopeSimple,
  FolderUser,
  Folders,
  House,
  MagnifyingGlass,
  BellSimple,
  ChartBar,
  Printer,
  Question,
  Receipt,
  SignOut,
  Tray,
  GearSix,
  Gavel,
} from "@phosphor-icons/react";
import useAuth from "../../hooks/useAuth";
import { logout } from "../../slice/AuthSlice";
import { markSignedOut } from "../../utils/signedOutFlag";
import { fetchFeatureFlags } from "../../slice/FeatureFlagsSlice";
import {
  fetchInboxUnreadCount,
  selectInboxUnreadCount,
} from "../../slice/InboxSlice";
import {
  fetchFeeNoticeAvailability,
  selectFeeNoticeAvailable,
} from "../../slice/FeeNoticeSlice";
import {
  fetchIntakeReviewAvailability,
  selectIntakeReviewAvailable,
} from "../../slice/IntakeReviewSlice";
import {
  MoreSheetItem,
  MoreSheetList,
  TabBadge,
  TabBarWrapper,
  TabButton,
  TabIconWrap,
  TabLabel,
} from "./MobileNavStyled";

/**
 * Bottom tab navigation for the mobile shell (Mobile DMS v1, Phase 1).
 *
 * Renders in place of `PrimarySideBar` below the mobile breakpoint —
 * `Layout` mounts exactly one of the two, so cross-cutting mount
 * effects the sidebar owns on desktop (feature flags, inbox unread
 * polling) are duplicated here rather than hoisted, keeping the
 * desktop code path untouched.
 *
 * Primary tabs mirror the v1 mobile scope (inbox triage, search,
 * matters); everything else lives behind "More" so the bar stays
 * thumb-sized.
 */

// Matches the sidebar's refresh cadence — see PrimarySideBar.js for
// the rationale on the 60s interval.
const INBOX_UNREAD_REFRESH_INTERVAL_MS = 60 * 1000;

const PRIMARY_TABS = [
  { key: "home", label: "Home", path: "/home", Icon: House },
  { key: "inbox", label: "Inbox", path: "/inbox", Icon: EnvelopeSimple },
  { key: "search", label: "Search", path: "/search", Icon: MagnifyingGlass },
  { key: "matters", label: "Matters", path: "/matter", Icon: Folders },
];

const getActiveKey = (pathname) => {
  if (pathname.startsWith("/inbox")) return "inbox";
  if (pathname.startsWith("/search")) return "search";
  if (pathname.startsWith("/matter")) return "matters";
  if (pathname.startsWith("/home")) return "home";
  if (pathname.startsWith("/brief-bank")) return "brief-bank";
  if (pathname.startsWith("/articles-bank")) return "articles-bank";
  if (pathname.startsWith("/my-files")) return "my-files";
  if (pathname.startsWith("/mail-intake")) return "mail-intake";
  if (pathname.startsWith("/fee-notices")) return "fee-notices";
  if (pathname.startsWith("/fax")) return "fax";
  if (pathname.startsWith("/notifications")) return "notifications";
  if (pathname.startsWith("/help")) return "help";
  if (pathname.startsWith("/partners")) return "partners";
  if (pathname.startsWith("/settings")) return "settings";
  return null;
};

const MobileTabBar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, hasCapability } = useAuth();
  const partnerDashboardAvailable =
    typeof hasCapability === "function" && hasCapability("reports.partner_dashboard");
  const logoutStatus = useSelector((state) => state.auth?.logoutStatus);
  const inboxUnreadCount = useSelector(selectInboxUnreadCount);
  const feeNoticeAvailable = useSelector(selectFeeNoticeAvailable);
  const intakeReviewAvailable = useSelector(selectIntakeReviewAvailable);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchFeatureFlags());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchFeeNoticeAvailability());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchIntakeReviewAvailability());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchInboxUnreadCount());
    const intervalId = setInterval(() => {
      dispatch(fetchInboxUnreadCount());
    }, INBOX_UNREAD_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [dispatch]);

  const activeKey = getActiveKey(location.pathname);
  // Highlight "More" when the current page lives inside the sheet.
  const moreActive =
    activeKey != null &&
    !PRIMARY_TABS.some((tab) => tab.key === activeKey);

  const handleNavigate = (path) => {
    setMoreOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
    } catch (error) {
      console.error("Logout failed:", error);
    }
    // Same contract as PrimarySideBar: the flag stops AuthRedirect from
    // silently re-entering Salesforce OAuth on the next landing.
    markSignedOut();
    globalThis.location.href = "/";
  };

  const moreItems = useMemo(() => {
    const items = [
      { key: "brief-bank", label: "Brief Bank", path: "/brief-bank", Icon: Books },
      { key: "articles-bank", label: "Articles Bank", path: "/articles-bank", Icon: Article },
      { key: "my-files", label: "My Files", path: "/my-files", Icon: FolderUser },
      {
        key: "notifications",
        label: "Notifications",
        path: "/notifications",
        Icon: BellSimple,
      },
      { key: "mail-intake", label: "Mail Intake", path: "/mail-intake", Icon: Tray },
      { key: "fax", label: "Fax", path: "/fax", Icon: Printer },
      { key: "help", label: "Help", path: "/help", Icon: Question },
    ];
    if (feeNoticeAvailable) {
      items.push({
        key: "fee-notices",
        label: "Fee Notices",
        path: "/fee-notices",
        Icon: Receipt,
      });
    }
    if (intakeReviewAvailable) {
      items.push({
        key: "intake-review",
        label: "Intake Review",
        path: "/intake-review",
        Icon: Gavel,
      });
    }
    if (partnerDashboardAvailable) {
      items.push({
        key: "partners",
        label: "Partners",
        path: "/partners",
        Icon: ChartBar,
      });
    }
    if (isAdmin) {
      items.push({
        key: "settings",
        label: "Settings",
        path: "/settings",
        Icon: GearSix,
      });
    }
    return items;
  }, [isAdmin, partnerDashboardAvailable, feeNoticeAvailable, intakeReviewAvailable]);

  return (
    <>
      <TabBarWrapper aria-label="Primary navigation">
        {PRIMARY_TABS.map(({ key, label, path, Icon }) => {
          const active = activeKey === key;
          const showBadge = key === "inbox" && inboxUnreadCount > 0;
          return (
            <TabButton
              key={key}
              type="button"
              $active={active}
              onClick={() => handleNavigate(path)}
              aria-label={
                showBadge ? `${label} — ${inboxUnreadCount} unread` : label
              }
              aria-current={active ? "page" : undefined}
            >
              <TabIconWrap>
                <Icon size={22} weight={active ? "fill" : "regular"} />
                {showBadge && (
                  <TabBadge aria-hidden="true">
                    {inboxUnreadCount > 99 ? "99+" : inboxUnreadCount}
                  </TabBadge>
                )}
              </TabIconWrap>
              <TabLabel $active={active}>{label}</TabLabel>
            </TabButton>
          );
        })}
        <TabButton
          type="button"
          $active={moreActive}
          onClick={() => setMoreOpen(true)}
          aria-label="More navigation options"
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
        >
          <TabIconWrap>
            <DotsThreeOutline
              size={22}
              weight={moreActive ? "fill" : "regular"}
            />
          </TabIconWrap>
          <TabLabel $active={moreActive}>More</TabLabel>
        </TabButton>
      </TabBarWrapper>

      <Drawer
        title="More"
        placement="bottom"
        height="auto"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        styles={{ body: { padding: "4px 16px 16px" } }}
      >
        <MoreSheetList>
          {moreItems.map(({ key, label, path, Icon }) => (
            <MoreSheetItem
              key={key}
              type="button"
              $active={activeKey === key}
              onClick={() => handleNavigate(path)}
            >
              <Icon size={20} weight={activeKey === key ? "fill" : "regular"} />
              {label}
            </MoreSheetItem>
          ))}
          <MoreSheetItem
            type="button"
            $danger
            onClick={handleLogout}
            disabled={logoutStatus === "loading"}
          >
            <SignOut size={20} />
            {logoutStatus === "loading" ? "Signing out…" : "Sign out"}
          </MoreSheetItem>
        </MoreSheetList>
      </Drawer>
    </>
  );
};

export default MobileTabBar;
